import { useState, useEffect, useRef, useCallback } from 'react'
import { estimatesService } from '@/services/estimates-service'
import type { OptionRow } from '@/types'

/* ── Icons ── */

const ListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
)

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10" />
    <path d="M15 9l-6 6M9 9l6 6" />
  </svg>
)

/* ── Helpers ── */

let _nextId = 1
function nextId() {
  return `opt-${Date.now()}-${_nextId++}`
}

function emptyRow(): OptionRow {
  return { id: nextId(), name: '', description: '', cost: 0 }
}

function formatCost(value: number): string {
  if (!value) return ''
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatWhileTyping(input: string): string {
  const stripped = input.replace(/[^0-9.]/g, '')
  if (!stripped) return ''
  const parts = stripped.split('.')
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const decPart = parts.length > 1 ? '.' + parts[1].slice(0, 2) : ''
  return '$' + intPart + decPart
}

/* ── Component ── */

interface OptionsPanelProps {
  estimateId: string
  initialData?: OptionRow[]
  onRowsChange?: (rows: OptionRow[]) => void
}

export function OptionsPanel({ estimateId, initialData, onRowsChange }: OptionsPanelProps) {
  const [rows, setRows] = useState<OptionRow[]>(() =>
    initialData && initialData.length > 0 ? initialData : []
  )
  const [loaded, setLoaded] = useState(false)
  const [costInputValues, setCostInputValues] = useState<Record<string, string>>({})
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Ref-mirrors `rows` so flushSave can always read the latest value without
  // needing to re-create on every render or being captured in stale closures.
  const rowsRef = useRef<OptionRow[]>(rows)
  rowsRef.current = rows

  useEffect(() => {
    if (initialData && !loaded) {
      setRows(initialData.length > 0 ? initialData : [])
      setLoaded(true)
    }
  }, [initialData, loaded])

  const save = useCallback(
    (updatedRows: OptionRow[]) => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(async () => {
        try {
          await estimatesService.update(estimateId, { optionsData: updatedRows })
          onRowsChange?.(updatedRows)
        } catch {
          // silent
        }
      }, 500)
    },
    [estimateId, onRowsChange]
  )

  // Force-flushes a pending debounced save right now. Wire to onBlur so the
  // change is on the server before the user can navigate / open the proposal
  // preview, instead of waiting 500 ms for the debounce.
  const flushSave = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current)
      saveTimer.current = null
    }
    try {
      await estimatesService.update(estimateId, { optionsData: rowsRef.current })
      onRowsChange?.(rowsRef.current)
    } catch {
      // silent
    }
  }, [estimateId, onRowsChange])

  useEffect(() => {
    return () => {
      // Flush a pending debounced save on unmount (e.g. switching tabs within
      // the 500ms window) instead of dropping it. Fire-and-forget — the panel is
      // going away; we only need the PUT to go out with the latest rows.
      if (saveTimer.current) {
        clearTimeout(saveTimer.current)
        saveTimer.current = null
        estimatesService.update(estimateId, { optionsData: rowsRef.current }).catch(() => {})
      }
    }
  }, [estimateId])

  const handleAdd = () => {
    const updated = [...rows, emptyRow()]
    setRows(updated)
    save(updated)
  }

  const handleDelete = (id: string) => {
    const updated = rows.filter((r) => r.id !== id)
    setRows(updated)
    save(updated)
  }

  const handleFieldChange = (id: string, field: keyof OptionRow, value: string | number) => {
    const updated = rows.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    setRows(updated)
    save(updated)
  }

  const handleCostBlur = (id: string, rawValue: string) => {
    const num = parseFloat(rawValue.replace(/[^0-9.-]/g, '')) || 0
    handleFieldChange(id, 'cost', num)
    setCostInputValues(prev => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    // Persist the change immediately so a click straight to Preview Proposal
    // doesn't race the 500 ms debounce. handleFieldChange just set state via
    // a debounced save; flushSave cancels that timer and PUTs the latest rows.
    flushSave()
  }

  return (
    <div className="sub-panel">
      {/* Header */}
      <div className="sub-header">
        <div className="sub-header-left">
          <ListIcon />
          <span className="sub-title">Options</span>
        </div>
        <button className="sub-add-btn" onClick={handleAdd}>
          <PlusIcon />
          <span>Add Option</span>
        </button>
      </div>

      {/* Table */}
      {rows.length > 0 ? (
        <div className="sub-table-wrap">
          <table className="sc-table sub-table">
            <thead>
              <tr>
                <th className="opt-col-name">OPTION NAME</th>
                <th className="opt-col-desc">DESCRIPTION</th>
                <th className="opt-col-cost">COST</th>
                <th className="sub-col-action"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="opt-col-name">
                    <input
                      className="sc-input"
                      type="text"
                      value={row.name}
                      placeholder="Option name"
                      onChange={(e) => handleFieldChange(row.id, 'name', e.target.value)}
                      onBlur={flushSave}
                    />
                  </td>
                  <td className="opt-col-desc">
                    <textarea
                      className="sc-input opt-desc-textarea"
                      value={row.description}
                      placeholder="Description"
                      rows={2}
                      onChange={(e) => handleFieldChange(row.id, 'description', e.target.value)}
                      onBlur={flushSave}
                    />
                  </td>
                  <td className="opt-col-cost">
                    <input
                      className="sc-input sub-num"
                      type="text"
                      value={row.id in costInputValues ? costInputValues[row.id] : formatCost(row.cost)}
                      placeholder="$0.00"
                      onFocus={(e) => {
                        const initial = row.cost ? formatWhileTyping(String(row.cost)) : ''
                        setCostInputValues(prev => ({ ...prev, [row.id]: initial }))
                        setTimeout(() => e.target.select(), 0)
                      }}
                      onChange={(e) => {
                        const formatted = formatWhileTyping(e.target.value)
                        setCostInputValues(prev => ({ ...prev, [row.id]: formatted }))
                        const num = parseFloat(e.target.value.replace(/[^0-9.]/g, '')) || 0
                        const updated = rows.map((r) => r.id === row.id ? { ...r, cost: num } : r)
                        setRows(updated)
                      }}
                      onBlur={(e) => handleCostBlur(row.id, e.target.value)}
                    />
                  </td>
                  <td>
                    <button className="sc-icon-btn" onClick={() => handleDelete(row.id)}>
                      <DeleteIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="sub-empty">
          <ListIcon />
          <p>No options added yet</p>
          <button className="sub-empty-btn" onClick={handleAdd}>
            <PlusIcon />
            Add Option
          </button>
        </div>
      )}
    </div>
  )
}
